import { API } from "../api";

export const authApi = API.injectEndpoints({
  endpoints: (builder) => ({
    userLogin: builder.mutation({
      query: (body) => ({
        url: "/onboard/cooperatives/login",
        method: "POST",
        body: {
          company_id: body.company_id,
          identifier: body.identifier, // Changed from email to identifier
          password: body.password,
        },
      }),
    }),
    getCoop: builder.query({
      query: ({ domain_url }) => ({
        url: `/onboard/getCooperative?domain_url=${domain_url}`,
        method: "GET",
      }),
    }),
    getCurrentUserProfile: builder.query({
      query: (body) => ({
        url: "/cooperatives/profile?guard=staff",
        method: "GET",
        body,
      }),
    }),
    getCurrentMemberProfile: builder.query({
      query: () => ({
        url: "/members/profile?guard=member",
        method: "GET",
      }),
      transformResponse: (responseData) => responseData.data,
      providesTags: [{ type: "Member", id: "CURRENT_PROFILE" }],
    }),
    updateMemberProfile: builder.mutation({
      query: (body) => ({
        url: "/members/profile?guard=member",
        method: "PATCH",
        body: body,
      }),
      transformResponse: (responseData) => responseData.data,
      invalidatesTags: [{ type: "Member", id: "CURRENT_PROFILE" }],
    }),
    // Forgot Password - Send OTP (SMS)
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: "/onboard/cooperatives/forgot_password/sendRecoveryPin",
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      // Don't transform response - return full response structure to handle status: false errors
    }),
    // Forgot Password - Resend OTP via WhatsApp
    resendOTPViaWhatsApp: builder.mutation({
      query: (body) => ({
        url: "/onboard/cooperatives/forgot_password/resend-via-whatsapp",
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      // Don't transform response - return full response structure to handle status: false errors
    }),
    // Reset Password - Check the OTP without changing the password
    verifyResetOtp: builder.mutation({
      query: ({ companyId, userId, otp }) => ({
        url: `/onboard/cooperatives/reset/${companyId}/${userId}/verify-otp`,
        method: "POST",
        body: { otp },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    // Reset Password - Set new password with OTP
    resetPassword: builder.mutation({
      query: ({ companyId, userId, email, ...body }) => ({
        url: `/onboard/cooperatives/reset/${companyId}/${userId}/${encodeURIComponent(email)}/set-password`,
        method: "PUT",
        body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (responseData) => responseData?.data,
    }),
    // Public endpoint to get compulsory fees for member registration
    getPublicCompulsoryFees: builder.query({
      query: (companyId) => ({
        url: `/onboard/getCompulsoryFee?company_id=${companyId}`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data || {},
    }),
    // Public endpoint to get branches by domain_url
    getPublicBranches: builder.query({
      query: (domainUrl) => ({
        url: `/onboard/getBranches?domain_url=${domainUrl}`,
        method: "GET",
      }),
      transformResponse: (responseData) => {
        // RTK Query's fetchBaseQuery returns the parsed JSON response body directly
        // So responseData is the full API response: { status: true, message: "...", data: [...] }
        // Handle case where responseData is already an array (shouldn't happen but be safe)
        if (Array.isArray(responseData)) {
          return responseData;
        }
        // Extract data array from JsonResponseAPI format: { status, message, data: [...] }
        // The response structure is: { status: true, message: "...", data: [...] }
        if (responseData && typeof responseData === 'object' && 'data' in responseData) {
          const branches = responseData.data;
          return Array.isArray(branches) ? branches : [];
        }
        // Fallback: return empty array if structure is unexpected
        console.warn('Unexpected response structure for getPublicBranches:', responseData);
        return [];
      },
    }),
    // Initialize Paystack payment for public member signup
    initiateSignupPayment: builder.mutation({
      query: (body) => ({
        url: "/onboard/cooperatives/initiate-signup-payment",
        method: "POST",
        body: body,
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (responseData) => responseData?.data || {},
    }),
    // Complete signup after successful payment
    completeSignupAfterPayment: builder.mutation({
      query: (body) => ({
        url: "/onboard/cooperatives/signup",
        method: "POST",
        body: body,
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (responseData) => responseData?.data || {},
    }),
  }),
});

export const {
  useUserLoginMutation,
  useGetCoopQuery,
  useGetCurrentUserProfileQuery,
  useLazyGetCurrentMemberProfileQuery,
  useLazyGetCurrentUserProfileQuery,
  useGetCurrentMemberProfileQuery,
  useUpdateMemberProfileMutation,
  useForgotPasswordMutation,
  useResendOTPViaWhatsAppMutation,
  useResetPasswordMutation,
  useVerifyResetOtpMutation,
  useGetPublicCompulsoryFeesQuery,
  useGetPublicBranchesQuery,
  useInitiateSignupPaymentMutation,
  useCompleteSignupAfterPaymentMutation,
} = authApi;
